import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { Button } from "@/components/ui/button";
import { InfiniteScroll } from "@/components/InfiniteScroll";
import { VoucherCard } from "@/components/vouchers/VoucherCard";
import { VoucherFilters } from "@/components/vouchers/VoucherFilters";
import { VoucherBulkActions } from "@/components/vouchers/VoucherBulkActions";
import { createVouchersService } from "@/services/admin/vouchers/vouchers.service";
import { Voucher, VoucherFilters as VoucherFiltersType, BulkVoucherResponse } from "@/services/admin/vouchers/vouchers.interfaces";
import { Plus, RefreshCw } from "lucide-react";
import { useLoading } from "@/lib/LoadingHook";
import { useRefresher } from "@/components/grids/DataGridComponent";

const AdminVouchersPage: React.FC = () => {
  const navigate = useNavigate();
  const vouchersService = createVouchersService();

  // Hooks obrigatórios para InfiniteScroll
  const { isLoading } = useLoading();
  const refreshHandle = useRefresher();

  // Estados que permanecem (não gerenciados pelo InfiniteScroll)
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<VoucherFiltersType>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Função adaptada para InfiniteScroll
  const fetchVouchersForInfiniteScroll = async (page: number): Promise<Voucher[]> => {
    try {
      setError(null);
      const response = await vouchersService.getVouchers(page, 12, filters);
      return response.vouchers;
    } catch (err) {
      setError('Erro ao carregar vouchers');
      console.error('Failed to fetch vouchers:', err);
      return [];
    }
  };

  // Funções para InfiniteScroll
  const renderVoucher = (voucher: Voucher, key: React.Key) => (
    <VoucherCard
      key={key}
      voucher={voucher}
      onEdit={handleEdit}
      onDelete={(id) => handleBulkDelete([id])}
      onActivate={(id) => handleBulkActivate([id])}
      onDeactivate={(id) => handleBulkDeactivate([id])}
      isSelected={selectedIds.includes(voucher.id)}
      onSelect={handleSelect}
      showActions={true}
    />
  );

  const getVoucherKey = (voucher: Voucher) => voucher.id;

  // Reset quando filtros mudam
  useEffect(() => {
    refreshHandle.refresh();
  }, [filters]);

  const handleFiltersChange = (newFilters: VoucherFiltersType) => {
    setFilters(newFilters);
    setSelectedIds([]);
  };

  const handleFiltersReset = () => {
    setFilters({});
    setSelectedIds([]);
  };

  const handleSelect = (id: string, selected: boolean) => {
    setSelectedIds(prev => 
      selected 
        ? [...prev, id]
        : prev.filter(vId => vId !== id)
    );
  };

  const handleBulkActivate = async (ids: string[]): Promise<BulkVoucherResponse> => {
    const result = await vouchersService.bulkActivateVouchers(ids);
    refreshHandle.refresh();
    return result;
  };

  const handleBulkDeactivate = async (ids: string[]): Promise<BulkVoucherResponse> => {
    const result = await vouchersService.bulkDeactivateVouchers(ids);
    refreshHandle.refresh();
    return result;
  };

  const handleBulkDelete = async (ids: string[]): Promise<BulkVoucherResponse> => {
    const result = await vouchersService.bulkDeleteVouchers(ids);
    refreshHandle.refresh();
    setSelectedIds([]);
    return result;
  };

  const handleBulkExtendValidity = async (ids: string[], newValidUntil: string): Promise<BulkVoucherResponse> => {
    const result = await vouchersService.bulkExtendValidity(ids, newValidUntil);
    refreshHandle.refresh();
    return result;
  };

  const handleExport = async (ids: string[]) => {
    try {
      const blob = await vouchersService.exportVouchers(
        { format: 'csv', columns: ['code', 'name', 'status', 'discountType', 'currentUses', 'validUntil'] },
        filters,
        ids
      );
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vouchers-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro ao exportar vouchers:', error);
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/admin/vouchers/${id}`);
  };

  const handleCreate = () => {
    navigate('/admin/vouchers/create');
  };

  const handleRefresh = () => {
    refreshHandle.refresh();
  };

  return (
    <AdminPageLayout title="Vouchers">
      <main className="py-6 space-y-6">
        {/* Header com ações */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Vouchers</h1>
            <p className="text-gray-600">Gerencie cupons de desconto e promoções</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Voucher
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <VoucherFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleFiltersReset}
        />

        {/* Ações em lote */}
        <VoucherBulkActions
          selectedIds={selectedIds}
          onBulkActivate={handleBulkActivate}
          onBulkDeactivate={handleBulkDeactivate}
          onBulkDelete={handleBulkDelete}
          onBulkExtendValidity={handleBulkExtendValidity}
          onExport={handleExport}
          isLoading={isLoading}
        />

        {/* Lista de vouchers com InfiniteScroll */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <InfiniteScroll<Voucher>
          refreshHandle={refreshHandle}
          fetchItems={fetchVouchersForInfiniteScroll}
          renderItem={renderVoucher}
          itemKey={getVoucherKey}
          layout="grid"
          gridCols={{ default: 1, lg: 3 }}
          showRefreshButton={true}
        />
      </main>
    </AdminPageLayout>
  );
};

export default AdminVouchersPage;
